import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import InfiniteSelect from '../../../../components/InfiniteSelect';
import api from '../../../../config/axios';

import {
  ModalOverlay,
  ModalContent,
  CloseButton,
  Input,
  Label,
  FieldGroup,
  ButtonGroup,
  Tag,
  TagsContainer,
  GradButton,
  GradContainer,
  ImportButton,
  CancelButton,
  SaveButton,
  SuggestionsContainer,
  SuggestionsList,
  SuggestionItem,
  SwitchContainer,
  SwitchLabel,
  SwitchBox,
  SwitchInput,
  SwitchSlider,
} from './stylemodal';

import CloseIcon from '../../../../assets/produtcss/close.svg'
import importIcon from '../../../../assets/produtcss/importtwo.svg'
import saveIcon from '../../../../assets/produtcss/saveprodut.svg'

import ImportPatternModal from '../Importmodal/ImportPatternModal'

const fadeInOut = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  10% { opacity: 1; transform: translateY(0); }
  90% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(20px); }
`;

const NotificationContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #4CAF50; /* Verde para sucesso */
  color: white;
  padding: 15px 25px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: ${fadeInOut} 4s ease-in-out forwards; /* 4 segundos de animação */
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CheckIcon = styled.img`
  width: 20px;
  height: 20px;
`;

interface CollectionBackend {
  id: number;
  name: string;
}

interface FamilyBackend {
  id: number;
  name: string;
}

interface TypeBackend {
  id: number;
  name: string;
}

interface ColorBackend {
  id: number;
  name: string;
  hexCode?: string;
}

interface ProductToEditType {
  id?: number;
  code?: string;
  name: string;
  collection: string;
  family: string;
  type: string;
  colors: string[];
  gradValues: (string | number)[];
  pricing: string;
  status?: boolean;
  pipefyId?: string;
}

interface NewProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: /*(product: {
    id?: number;
    name: string;
    collection: string;
    family: string;
    type: string;
    colors: string[];
    gradValues: (string | number)[];
    pricing: string;
    status?: boolean;
  }) => Promise<void> |*/ any;
  productToEdit: ProductToEditType | null;
}

const NewProductModal: React.FC<NewProductModalProps> = ({
  isOpen,
  onClose,
  onSave,
  productToEdit,
}) => {
  const [name, setName] = useState('');
  const [collection, setCollection] = useState('');
  const [collectionId, setCollectionId] = useState<number | null>(null);
  const [family, setFamily] = useState('');
  const [familyId, setFamilyId] = useState<number | null>(null);
  const [type, setType] = useState('');
  const [typeId, setTypeId] = useState<number | null>(null);

  const [colors, setColors] = useState<ColorBackend[]>([]);
  const [colorInput, setColorInput] = useState('');

  const [colorSuggestions, setColorSuggestions] = useState<ColorBackend[]>([]);
  const [showColorSuggestions, setShowColorSuggestions] = useState(false);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const colorSuggestionsContainerRef = useRef<HTMLUListElement>(null);

  const [gradValues, setGradValues] = useState<(string | number)[]>([]);
  const [pricingInput, setPricingInput] = useState('');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const [collectionSuggestions, setCollectionSuggestions] = useState<
    CollectionBackend[]
  >([]);
  const [showCollectionSuggestions, setShowCollectionSuggestions] =
    useState(false);
  const collectionInputRef = useRef<HTMLInputElement>(null);
  const collectionSuggestionsContainerRef = useRef<HTMLUListElement>(null);

  const [familySuggestions, setFamilySuggestions] = useState<FamilyBackend[]>(
    [],
  );
  const [showFamilySuggestions, setShowFamilySuggestions] = useState(false);
  const familyInputRef = useRef<HTMLInputElement>(null);
  const familySuggestionsContainerRef = useRef<HTMLUListElement>(null);

  const [typeSuggestions, setTypeSuggestions] = useState<TypeBackend[]>([]);
  const [showTypeSuggestions, setShowTypeSuggestions] = useState(false);
  const typeInputRef = useRef<HTMLInputElement>(null);
  const typeSuggestionsContainerRef = useRef<HTMLUListElement>(null);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [createInPipefy, setCreateInPipefy] = useState(true); // Estado do switch

  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
        setNotificationMessage('');
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  const debouncedFetchColorSuggestions = useCallback(
    (query: string) => {
      if (!familyId) {
        setColorSuggestions([]);
        return;
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await api.get(
            `/backend-fashion-orders/fashion-product/families/${familyId}/colors`,
            {
              params: { name: query }
            }
          );
          const data: { colors: ColorBackend[] } = response.data;
          setColorSuggestions(data.colors || []);
          setShowColorSuggestions((data.colors || []).length > 0);
        } catch (error) {
          console.error('Erro ao buscar sugestões de cor:', error);
          setColorSuggestions([]);
          setShowColorSuggestions(false);
        }
      }, 300);
    },
    [familyId],
  );

  const fetchTypeIdByName = useCallback(async (typeName: string, familyId: number) => {
    try {
      const response = await api.get(
        `/backend-fashion-orders/fashion-product/groups`,
        {
          params: {
            name: typeName,
            familyId: String(familyId)
          }
        }
      );
      const data: TypeBackend[] = response.data;
      const matchedType = data.find((t) => t.name === typeName);
      if (matchedType) {
        setTypeId(matchedType.id);
        return matchedType.id;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Erro ao buscar ID do tipo:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (productToEdit) {
        setName(productToEdit.name || '');
        setCollection(productToEdit.collection || '');
        setFamily(productToEdit.family || '');
        setType(productToEdit.type || '');
        setGradValues(productToEdit.gradValues || []);
        setPricingInput(productToEdit.pricing || '');

        const fetchIdsAndColors = async () => {
          try {
            console.log('[EDIT-PRODUCT] Iniciando busca para:', productToEdit);

            if (productToEdit.collection) {
              const collectionsRes = await api.get(
                `/backend-fashion-orders/fashion-product/collections?search=${encodeURIComponent(
                  productToEdit.collection,
                )}`,
              );
              const collectionsData: CollectionBackend[] = collectionsRes.data;
              console.log('[EDIT-PRODUCT] Collections:', collectionsData);

              const matchedCollection = collectionsData.find(
                (c) => c.name.trim().toLowerCase() === productToEdit.collection.trim().toLowerCase(),
              );

              if (matchedCollection) {
                console.log('[EDIT-PRODUCT] Collection encontrada:', matchedCollection);
                setCollectionId(matchedCollection.id);
                setCollection(matchedCollection.name);

                if (productToEdit.family) {
                  const familiesRes = await api.get(
                    `/backend-fashion-orders/fashion-product/families?search=${encodeURIComponent(
                      productToEdit.family,
                    )}&collectionId=${matchedCollection.id}`,
                  );
                  const familiesData: FamilyBackend[] = familiesRes.data;
                  console.log('[EDIT-PRODUCT] Families:', familiesData);

                  const matchedFamily = familiesData.find(
                    (f) => f.name.trim().toLowerCase() === productToEdit.family.trim().toLowerCase(),
                  );

                  if (matchedFamily) {
                    console.log('[EDIT-PRODUCT] Family encontrada:', matchedFamily);
                    setFamilyId(matchedFamily.id);
                    setFamily(matchedFamily.name);

                    const colorsRes = await api.get(
                      `/backend-fashion-orders/fashion-product/families/${matchedFamily.id}/colors`,
                    );
                    const colorsData: { colors: ColorBackend[] } = colorsRes.data;
                    console.log('[EDIT-PRODUCT] Colors:', colorsData);

                    const colorsWithIds = productToEdit.colors
                      .map((colorName) => {
                        const trimmedColorName = colorName.trim();
                        const matchedColor = (colorsData.colors || []).find(
                          (c) => c.name.trim().toLowerCase() === trimmedColorName.toLowerCase(),
                        );
                        return matchedColor || { name: colorName, id: -Math.random() };
                      });

                    const uniqueColors = Array.from(new Map(colorsWithIds.map(item => [item.id, item])).values());
                    setColors(uniqueColors);

                    if (productToEdit.type) {
                      await fetchTypeIdByName(productToEdit.type, matchedFamily.id);
                    }
                  } else {
                    console.warn('[EDIT-PRODUCT] Família não encontrada');
                  }
                } else {
                  console.warn('[EDIT-PRODUCT] Produto sem família');
                }
              } else {
                console.warn('[EDIT-PRODUCT] Coleção não encontrada');
              }
            } else {
              console.warn('[EDIT-PRODUCT] Produto sem coleção');
            }
          } catch (error) {
            console.error('[EDIT-PRODUCT] Erro:', error);
          }
        };

        fetchIdsAndColors();
      } else {
        setName('');
        setCollection('');
        setCollectionId(null);
        setFamily('');
        setFamilyId(null);
        setType('');
        setTypeId(null);
        setColors([]);
        setColorInput('');
        setGradValues([]);
        setPricingInput('');
      }
      setCollectionSuggestions([]);
      setShowCollectionSuggestions(false);
      setFamilySuggestions([]);
      setShowFamilySuggestions(false);
      setTypeSuggestions([]);
      setShowTypeSuggestions(false);
    }
  }, [productToEdit, isOpen, fetchTypeIdByName]);

  const debouncedFetchCollectionSuggestions = useCallback((query: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await api.get(
          `/backend-fashion-orders/fashion-product/collections`,
          {
            params: { name: query }
          }
        );
        const data: CollectionBackend[] = response.data;
        setCollectionSuggestions(data);
        setShowCollectionSuggestions(data.length > 0);
      } catch (error) {
        console.error('Erro ao buscar sugestões de coleção:', error);
        setCollectionSuggestions([]);
        setShowCollectionSuggestions(false);
      }
    }, 300);
  }, []);

  const debouncedFetchFamilySuggestions = useCallback(
    (query: string) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const params: any = { name: query };
          if (collectionId) {
            params.collectionId = String(collectionId);
          }
          const response = await api.get(
            `/backend-fashion-orders/fashion-product/families`,
            { params }
          );
          const data: FamilyBackend[] = response.data;
          setFamilySuggestions(data);
          setShowFamilySuggestions(data.length > 0);
        } catch (error) {
          console.error('Erro ao buscar sugestões de família:', error);
          setFamilySuggestions([]);
          setShowFamilySuggestions(false);
        }
      }, 300);
    },
    [collectionId],
  );

  const debouncedFetchTypeSuggestions = useCallback(
    (query: string) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const params: any = { name: query };
          if (familyId) {
            params.familyId = String(familyId);
          }
          const response = await api.get(
            `/backend-fashion-orders/fashion-product/groups`,
            { params }
          );
          const data: TypeBackend[] = response.data;
          setTypeSuggestions(data);
          setShowTypeSuggestions(data.length > 0);
        } catch (error) {
          console.error('Erro ao buscar sugestões de tipo:', error);
          setTypeSuggestions([]);
          setShowTypeSuggestions(false);
        }
      }, 300);
    },
    [familyId],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        collectionInputRef.current &&
        !collectionInputRef.current.contains(event.target as Node) &&
        collectionSuggestionsContainerRef.current &&
        !collectionSuggestionsContainerRef.current.contains(
          event.target as Node,
        )
      ) {
        setShowCollectionSuggestions(false);
      }
      if (
        familyInputRef.current &&
        !familyInputRef.current.contains(event.target as Node) &&
        familySuggestionsContainerRef.current &&
        !familySuggestionsContainerRef.current.contains(event.target as Node)
      ) {
        setShowFamilySuggestions(false);
      }
      if (
        typeInputRef.current &&
        !typeInputRef.current.contains(event.target as Node) &&
        typeSuggestionsContainerRef.current &&
        !typeSuggestionsContainerRef.current.contains(event.target as Node)
      ) {
        setShowTypeSuggestions(false);
      }
      if (
        colorInputRef.current &&
        !colorInputRef.current.contains(event.target as Node) &&
        colorSuggestionsContainerRef.current &&
        !colorSuggestionsContainerRef.current.contains(event.target as Node)
      ) {
        setShowColorSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectColorSuggestion = (selectedColor: ColorBackend) => {
    if (!colors.some((color) => color.id === selectedColor.id)) {
      setColors((prevColors) => [...prevColors, selectedColor]);
    }
    setColorInput('');
    setColorSuggestions([]);
    setShowColorSuggestions(false);
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColorValue = e.target.value;
    setColorInput(newColorValue);
    debouncedFetchColorSuggestions(newColorValue);
  };

  const handleColorFocus = () => {
    debouncedFetchColorSuggestions(colorInput);
    setShowColorSuggestions(true);
  };

  const handleAddColor = () => {
    const trimmed = colorInput.trim();
    if (trimmed && !colors.some(c => c.name === trimmed)) {
      const newColor = { name: trimmed, id: -Math.random() };
      setColors(prevColors => [...prevColors, newColor]);
    }
    setColorInput('');
  };

  const handleRemoveColor = (colorToRemove: ColorBackend) => {
    setColors(colors.filter((color) => color.id !== colorToRemove.id));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddColor();
    }
  };

  const toggleGrad = (val: string | number) => {
    if (gradValues.includes(val)) {
      setGradValues(gradValues.filter((v) => v !== val));
    } else {
      setGradValues(
        [...gradValues, val].sort((a, b) => {
          const isANumber = typeof a === 'number';
          const isBNumber = typeof b === 'number';

          if (isANumber && isBNumber) {
            return a - b;
          }

          if (isANumber) {
            return -1;
          }

          if (isBNumber) {
            return 1;
          }

          return String(a).localeCompare(String(b));
        }),
      );
    }
  };

  const handleSave = async () => {
    if (
      !name.trim() ||
      !collection.trim() ||
      !family.trim() ||
      !type.trim() ||
      !pricingInput.trim()
    ) {
      alert(
        'Por favor, preencha todos os campos obrigatórios (Nome, Coleção, Família, Tipo, Precificação).',
      );
      return;
    }

    const cleanPricing = pricingInput
      .replace('R$', '')
      .replace(/\./g, '')
      .replace(',', '.')
      .trim();
    const parsedPricing = parseFloat(cleanPricing);

    if (isNaN(parsedPricing)) {
      alert(
        'Precificação inválida. Por favor, insira um valor numérico válido.',
      );
      return;
    }

    const colorNames = colors.map((color) => color.name);

    const productData = {
      id: productToEdit?.id,
      name,
      collection,
      family,
      type,
      colors: colorNames,
      gradValues,
      pricing: parsedPricing.toString(),
      status:
        productToEdit?.id !== undefined
          ? productToEdit.status !== undefined
            ? productToEdit.status
            : true
          : true,
      pipefyId: productToEdit?.pipefyId || '',
      createInPipefy, // Adicionar flag para controlar cadastro no Pipefy
    };

    try {
      await onSave(productData);
      setNotificationMessage('Produto salvo com sucesso!');
      setShowNotification(true);
      onClose();
    } catch (error) {
      console.error('Erro ao tentar salvar produto no modal:', error);
      alert('Erro ao salvar produto. Verifique o console para mais detalhes.');
      setNotificationMessage('Erro ao salvar produto!');
      setShowNotification(true);
    }
  };

  const handleSelectCollectionSuggestion = (
    selectedCollection: CollectionBackend,
  ) => {
    setCollection(selectedCollection.name);
    setCollectionId(selectedCollection.id);
    setFamily('');
    setFamilyId(null);
    setType('');
    setTypeId(null);
    setColors([]);
    setColorInput('');
    setShowCollectionSuggestions(false);
    setCollectionSuggestions([]);
  };

  const handleCollectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCollectionValue = e.target.value;
    setCollection(newCollectionValue);
    setCollectionId(null);
    setFamily('');
    setFamilyId(null);
    setType('');
    setTypeId(null);
    setColors([]);
    setColorInput('');
    debouncedFetchCollectionSuggestions(newCollectionValue);
  };

  const handleCollectionFocus = () => {
    debouncedFetchCollectionSuggestions(collection);
    setShowCollectionSuggestions(true);
  };

  const handleSelectFamilySuggestion = (selectedFamily: FamilyBackend) => {
    setFamily(selectedFamily.name);
    setFamilyId(selectedFamily.id);
    setType('');
    setTypeId(null);
    setColors([]);
    setColorInput('');
    setShowFamilySuggestions(false);
    setFamilySuggestions([]);
  };

  const handleFamilyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFamilyValue = e.target.value;
    setFamily(newFamilyValue);
    setFamilyId(null);
    setType('');
    setTypeId(null);
    setColors([]);
    setColorInput('');
    debouncedFetchFamilySuggestions(newFamilyValue);
  };

  const handleFamilyFocus = () => {
    debouncedFetchFamilySuggestions(family);
    setShowFamilySuggestions(true);
  };

  const handleSelectTypeSuggestion = (selectedType: TypeBackend) => {
    setType(selectedType.name);
    setTypeId(selectedType.id);
    setShowTypeSuggestions(false);
    setTypeSuggestions([]);
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTypeValue = e.target.value;
    setTypeId(null);
    setType(newTypeValue);
    debouncedFetchTypeSuggestions(newTypeValue);
  };

  const handleTypeFocus = () => {
    debouncedFetchTypeSuggestions(type);
    setShowTypeSuggestions(true);
  };

  const formatPricingInput = (value: string) => {
    let val = value.replace(/[^0-9,.]/g, '');
    val = val.replace(/,/g, '.');

    const parts = val.split('.');
    if (parts.length > 2) {
      val = parts[0] + '.' + parts.slice(1).join('');
    }

    if (val.startsWith('0') && val.length > 1 && !val.includes('.')) {
      val = val.substring(1);
    } else if (
      val.length > 1 &&
      val.includes('.') &&
      val.indexOf('.') === 1 &&
      val.startsWith('0')
    ) {
      // Mantém valores como "0.5" sem modificação
    } else if (val.startsWith('0') && val.length > 1 && val.charAt(1) !== '.') {
      val = parseFloat(val).toString();
    }

    if (val.includes('.')) {
      const [integer, decimal] = val.split('.');
      return `R$ ${integer.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        '.',
      )},${decimal.substring(0, 2)}`;
    } else if (val) {
      return `R$ ${val.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
    } else {
      return '';
    }
  };

  const handlePricingInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace('R$', '').replace(/\./g, '').trim();
    setPricingInput(formatPricingInput(rawValue));
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2>
            {productToEdit
              ? `Editar produto: ${productToEdit.code || ''}`
              : 'Novo produto'}
          </h2>
          <CloseButton onClick={onClose}>
            <img src={CloseIcon} alt='Fechar' />
          </CloseButton>
        </div>

        <Label>Nome do produto</Label>
        <Input
          placeholder='Digite aqui'
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
          required
        />

        <div style={{ marginTop: '20px' }}>
          <InfiniteSelect
            label="Coleção"
            placeholder="Digite ou selecione a coleção"
            value={collectionId ? { id: collectionId, name: collection } : null}
            onSelect={(option) => {
              if (option) {
                setCollection(option.name);
                setCollectionId(option.id);
                setFamily('');
                setFamilyId(null);
                setType('');
                setTypeId(null);
                setColors([]);
                setColorInput('');
              } else {
                setCollection('');
                setCollectionId(null);
                setFamily('');
                setFamilyId(null);
                setType('');
                setTypeId(null);
                setColors([]);
                setColorInput('');
              }
            }}
            mode="crud"
            allowCreate={true}
            allowDelete={true}
            endpointConfig={{
              baseUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders`,
              listPath: '/fashion-product/collections',
              createPath: '/fashion-product/collections',
              deletePath: '/fashion-product/collections',
              searchParam: 'search',
              limitParam: 'limit',
              offsetParam: 'offset',
              transformResponse: (data) => Array.isArray(data) ? data.map((item: any) => ({ id: item.id, name: item.name })) : [],
              transformCreateResponse: (data) => ({ id: data.id, name: data.name })
            }}
          />
        </div>

        <FieldGroup>
          <div>
            <InfiniteSelect
              key={`family-${collectionId || 'no-collection'}`}
              label="Família"
              placeholder="Digite ou selecione a família"
              value={familyId ? { id: familyId, name: family } : null}
              onSelect={(option) => {
                if (option) {
                  setFamily(option.name);
                  setFamilyId(option.id);
                  setType('');
                  setTypeId(null);
                  setColors([]);
                  setColorInput('');
                } else {
                  setFamily('');
                  setFamilyId(null);
                  setType('');
                  setTypeId(null);
                  setColors([]);
                  setColorInput('');
                }
              }}
              mode="crud"
              allowCreate={true}
              allowDelete={true}
              disabled={!collectionId}
              endpointConfig={{
                baseUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders`,
                listPath: '/fashion-product/families',
                createPath: '/fashion-product/families',
                deletePath: '/fashion-product/families',
                searchParam: 'search',
                limitParam: 'limit',
                offsetParam: 'offset',
                transformResponse: (data) => Array.isArray(data) ? data.map((item: any) => ({ id: item.id, name: item.name })) : [],
                transformCreateResponse: (data) => ({ id: data.id, name: data.name }),
                additionalParams: collectionId ? { collectionId: collectionId } : {}
              }}
            />
          </div>

          <div>
            <InfiniteSelect
              key={`group-${familyId || 'no-family'}`}
              label="Grupo"
              placeholder="Digite ou selecione o grupo"
              value={typeId ? { id: typeId, name: type } : null}
              onSelect={(option) => {
                if (option) {
                  setType(option.name);
                  setTypeId(option.id);
                } else {
                  setType('');
                  setTypeId(null);
                }
              }}
              mode="crud"
              allowCreate={true}
              allowDelete={true}
              disabled={!familyId}
              endpointConfig={{
                baseUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders`,
                listPath: '/fashion-product/groups',
                createPath: '/fashion-product/groups',
                deletePath: '/fashion-product/groups',
                searchParam: 'search',
                limitParam: 'limit',
                offsetParam: 'offset',
                transformResponse: (data) => Array.isArray(data) ? data.map((item: any) => ({ id: item.id, name: item.name })) : [],
                transformCreateResponse: (data) => ({ id: data.id, name: data.name }),
                additionalParams: familyId ? { familyId: familyId } : {}
              }}
            />
          </div>
        </FieldGroup>

        <div style={{ marginTop: '20px' }}>
          <InfiniteSelect
            key={`colors-${familyId || 'no-family'}`}
            label="Cores"
            placeholder="Digite ou selecione uma cor"
            value={null}
            onSelect={(option) => {
              if (option && !colors.find(c => c.id === option.id)) {
                setColors([...colors, { id: option.id, name: option.name }]);
              }
            }}
            mode="crud"
            allowCreate={true}
            allowDelete={true}
            disabled={!familyId}
            endpointConfig={{
              baseUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders`,
              listPath: '/fashion-product/colors',
              createPath: '/fashion-product/colors',
              deletePath: '/fashion-product/colors',
              searchParam: 'search',
              limitParam: 'limit',
              offsetParam: 'offset',
              transformResponse: (data) => Array.isArray(data) ? data.map((item: any) => ({ id: item.id, name: item.name })) : [],
              transformCreateResponse: (data) => ({ id: data.id, name: data.name }),
              additionalParams: familyId ? { familyId: familyId } : {}
            }}
          />
        </div>

        <TagsContainer>
          {colors.map((color) => (
            <Tag key={color.id} onClick={() => handleRemoveColor(color)}>
              {color.name}
              <span style={{ marginLeft: '5px', cursor: 'pointer' }}>x</span>
            </Tag>
          ))}
        </TagsContainer>

        <Label>Graduação</Label>
        <GradContainer>
          {["PP", "P", "M", "G", "GG", "EXG", 36, 38, 40, 42, 44, 46, 48, "Único"].map((value) => (
            <GradButton
              key={value}
              selected={gradValues.includes(value)}
              onClick={() => toggleGrad(value)}
            >
              {value}
            </GradButton>
          ))}
        </GradContainer>

        <Label>Precificação</Label>
        <Input
          placeholder='Digite aqui (ex: 120,00)'
          value={pricingInput}
          onChange={handlePricingInputChange}
          required
        />

        <SwitchContainer>
          <SwitchLabel htmlFor="createInPipefy">
            Criar produto no Pipefy
          </SwitchLabel>
          <SwitchBox>
            <SwitchInput
              type="checkbox"
              id="createInPipefy"
              checked={createInPipefy}
              onChange={(e) => setCreateInPipefy(e.target.checked)}
            />
            <SwitchSlider />
          </SwitchBox>
        </SwitchContainer>

        <ButtonGroup>
          <ImportButton onClick={() => setIsImportModalOpen(true)}>
            <img src={importIcon} alt='Importar' />
            Importar padrão
          </ImportButton>
          <CancelButton onClick={onClose}>Cancelar</CancelButton>
          <SaveButton onClick={handleSave}>
            <img src={saveIcon} alt='Salvar' />
            Salvar produto
          </SaveButton>
        </ButtonGroup>
      </ModalContent>

      <ImportPatternModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
      />

      {showNotification && (
        <NotificationContainer>
          <span>{notificationMessage}</span>
        </NotificationContainer>
      )}
    </ModalOverlay>
  );
};

export default NewProductModal;